import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    AudioElement,
    type ControllableElement,
    type CreateElementOptions,
    CreativeTypes,
    ElementTypes,
    ImageElement,
    ShapeTypes,
    VideoElement,
} from '@bynder-studio/render-core';
import { PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import useEditor from '~/hooks/useEditor';
import { getElementsFonts, getFonts } from 'packages/store/user/user.selectors';
import { getElementPropertiesFromSource } from '~/common/editor/helpers';
import { getDefaultFontId } from 'packages/pages/editor/utils/getDefaultFontId';

export type ElementCreator = {
    createTextElement: () => void;
    createEllipseElement: () => void;
    createRectangleElement: () => void;
    createVideoElement: (source: Source) => void;
    createImageElement: (source: Source) => void;
    createAudioElement: (source: any) => void;
};

type ElementToSubmit = {
    data: [ElementTypes, any, CreateElementOptions];
    onCreated?: (element: ControllableElement) => void;
};

type Source = {
    width: number;
    height: number;
    [key: string]: any; // todo: make it more specific
};

const DEFAULT_VIRTUAL_DATA = {
    bynderCollectionId: null,
    allowPersonalUpload: false,
};

export default function useElementCreator(): ElementCreator {
    const { creativeModel, manipulationRenderer, creativeType } = useEditor();
    const fonts = useSelector(getFonts);
    const elementsFonts = useSelector(getElementsFonts);

    /**
     * Creates an element from provided data,
     * executes provided callback if some update right after creation needed
     */
    const submitElement = useCallback(
        ({ data, onCreated }: ElementToSubmit) => {
            if (onCreated) {
                creativeModel.beginAccumulation();

                const element = creativeModel.createElement(...data);
                onCreated(element);

                creativeModel.endAccumulation();
            } else {
                creativeModel.createElement(...data);
            }

            manipulationRenderer?.redraw();
        },
        [creativeModel, manipulationRenderer],
    );

    const createSourceUpdater = useCallback(
        (type: ElementTypes.IMAGE | ElementTypes.VIDEO | ElementTypes.AUDIO, source: Source) =>
            (element: ImageElement | VideoElement | AudioElement) => {
                const updateData = getElementPropertiesFromSource(element, type, source);
                creativeModel.updateElement(element.id, updateData);
            },
        [creativeModel],
    );

    const createImageElement = useCallback(
        (source: Source) => {
            const type = ElementTypes.IMAGE;
            const rawParams = {
                name: 'New image element',
                startFrame: getStartFrame(),
                creativeType,
                naturalDimension: getNaturalDimension(source),
                virtualData: DEFAULT_VIRTUAL_DATA,
            };

            submitElement({
                data: [type, rawParams, {}],
                onCreated: createSourceUpdater(type, source),
            });
        },
        [manipulationRenderer, createSourceUpdater, submitElement],
    );

    const getStartFrame = useCallback(
        () =>
            manipulationRenderer instanceof PlaybackManipulationRenderer ? manipulationRenderer.getCurrentFrame() : 0,
        [manipulationRenderer],
    );

    const createVideoElement = useCallback(
        (source: Source) => {
            if (creativeType !== CreativeTypes.VIDEO) {
                return;
            }

            const type = ElementTypes.VIDEO;
            const rawParams = {
                name: 'New video element',
                startFrame: getStartFrame(),
                creativeType,
                naturalDimension: getNaturalDimension(source),
                virtualData: DEFAULT_VIRTUAL_DATA,
            };

            submitElement({
                data: [type, rawParams, {}],
                onCreated: createSourceUpdater(type, source),
            });
        },
        [manipulationRenderer, createSourceUpdater, submitElement],
    );

    const createAudioElement = useCallback(
        (source) => {
            if (creativeType !== CreativeTypes.VIDEO) {
                return;
            }

            const type = ElementTypes.AUDIO;
            const rawParams = {
                name: 'New audio element',
                startFrame: getStartFrame(),
                creativeType,
            };

            submitElement({
                data: [type, rawParams, {}],
                onCreated: createSourceUpdater(type, source),
            });
        },
        [creativeType, submitElement],
    );

    const createTextElement = useCallback(() => {
        const rawParams = {
            name: 'New text element',
            text: 'Add text',
            startFrame: getStartFrame(),
            fontId: getDefaultFontId(elementsFonts, fonts),
            creativeType,
        };

        submitElement({ data: [ElementTypes.TEXT, rawParams, {}] });
    }, [fonts, elementsFonts, manipulationRenderer, submitElement]);

    const createShapeElement = useCallback(
        (shapeType: ShapeTypes) => {
            const rawParams = {
                name: 'New shape element',
                startFrame: getStartFrame(),
                shapeType,
                creativeType,
            };

            submitElement({ data: [ElementTypes.SHAPE, rawParams, {}] });
        },
        [manipulationRenderer, submitElement],
    );

    const createRectangleElement = useCallback(() => createShapeElement(ShapeTypes.RECTANGLE), [createShapeElement]);
    const createEllipseElement = useCallback(() => createShapeElement(ShapeTypes.ELLIPSE), [createShapeElement]);

    const elementCreator = useMemo(
        () => ({
            createTextElement,
            createEllipseElement,
            createRectangleElement,
            createVideoElement,
            createImageElement,
            createAudioElement,
        }),
        [
            createTextElement,
            createEllipseElement,
            createRectangleElement,
            createVideoElement,
            createImageElement,
            createAudioElement,
        ],
    );

    return elementCreator;
}

function getNaturalDimension(source: Source) {
    return {
        width: source.width,
        height: source.height,
    };
}
